// Position in 3D space
export interface Position {
  x: number;
  y: number;
  z: number;
}

// Entity in the simulation
export interface Entity {
  id: string;
  position: Position;
}

// AI Model configuration for simulations
export interface AIModel {
  ai_name: string;
  topology_id: string;
  position: [number, number, number] | null;
  auto_spawn: boolean;
}

// Topology metadata
export interface Topology {
  id: string;
  name: string;
  file_path: string;
  description?: string;
  published: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
}

// Simulation metadata
export interface Simulation {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'maintenance' | 'private';
  is_private?: boolean;
  allowed_users?: number[] | null;
  ai_models?: AIModel[];
  created_by?: number;
  created_at?: string;
  updated_at?: string;
  sharing_link?: string;
  // Legacy fields for backward compatibility
  thumbnail?: string;
}

// Audio data (PCM samples)
export interface AudioData {
  samples: number[]; // length 2048, values [-1.0, 1.0]
}

// Legacy: Audio data (16 frequency bands) - deprecated
export interface AudioDataLegacy {
  amplitudes: number[]; // length 16, values [0.0-1.0]
}

// Client → Server message types
// Note: audio_output is sent via binary WebSocket frames, not JSON
export type ClientMessage =
  | {
      type: 'join';
      entity_id: string;
      position: number[]; // [x, y, z]
      token: string;
      simulation_id: string;
    }
  | {
      type: 'update_position';
      position: number[]; // [x, y, z]
    }
  | {
      type: 'list_entities';
    }
  | {
      type: 'inject_audio';
      entity_id: string;
      samples: number[]; // length 2048
    }
  | {
      type: 'move_entity';
      entity_id: string;
      position: number[]; // [x, y, z]
    };

// Server → Client message types
export type ServerMessage =
  | {
      type: 'joined';
      entity_id: string;
    }
  | {
      type: 'audio_input';
      samples: number[]; // length 2048
    }
  | {
      type: 'entity_list';
      entities: Array<{
        id: string;
        position: number[]; // [x, y, z]
      }>;
    }
  | {
      type: 'entity_joined';
      entity_id: string;
      position: number[]; // [x, y, z]
    }
  | {
      type: 'entity_left';
      entity_id: string;
    }
  | {
      type: 'position_update';
      entity_id: string;
      position: number[]; // [x, y, z]
    }
  | {
      type: 'error';
      message: string;
    };

// Connection status
export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  ERROR = 'error',
}

// Frequency bands (Hz)
export const FREQUENCY_BANDS = [
  80, 120, 180, 270, 400, 600, 900, 1300,
  2000, 3000, 4500, 6700, 10000, 12000, 15000, 18000
];

// Frequency band labels
export const FREQ_LABELS = [
  '80', '120', '180', '270', '400', '600', '900', '1.3k',
  '2k', '3k', '4.5k', '6.7k', '10k', '12k', '15k', '18k'
];

// Audio preset types
export type AudioPreset = 'silence' | 'flat' | 'bass' | 'treble' | 'random';
